import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BreakdownStateController } from '../../services/breakdown-state.js';
import * as lifeplanApi from '../../services/lifeplan-api.js';
import type { BreakdownLevel, BreakdownHierarchyNode } from '../../services/breakdown-api.js';
import './cp-breakdown-workflow.js';
import './cp-goal-hierarchy.js';
import './cp-goal-edit-modal.js';

type ActiveTab = 'workflows' | 'overview';

interface BreakdownLevelInfo {
  level: BreakdownLevel;
  label: string;
  description: string;
  prerequisiteLevel: BreakdownLevel | null;
  prerequisiteLabel: string;
}

const BREAKDOWN_LEVELS: BreakdownLevelInfo[] = [
  {
    level: 'monthly',
    label: 'Monthly Breakdown',
    description: 'Monatsziele aus dem Lebensplan ableiten',
    prerequisiteLevel: null,
    prerequisiteLabel: '',
  },
  {
    level: 'weekly',
    label: 'Weekly Breakdown',
    description: 'Wochenziele aus den Monatszielen ableiten',
    prerequisiteLevel: 'monthly',
    prerequisiteLabel: 'Zuerst Monthly Breakdown durchfuehren',
  },
  {
    level: 'daily',
    label: 'Daily Breakdown',
    description: 'Tagesziele aus den Wochenzielen ableiten',
    prerequisiteLevel: 'weekly',
    prerequisiteLabel: 'Zuerst Weekly Breakdown durchfuehren',
  },
];

@customElement('cp-page-breakdowns')
export class CpPageBreakdowns extends LitElement {
  createRenderRoot() { return this; }

  @state() private _activeTab: ActiveTab = 'workflows';
  @state() private _configured = false;
  @state() private _configLoading = true;
  @state() private _activeWorkflow: BreakdownLevel | null = null;
  @state() private _editGoal: BreakdownHierarchyNode | null = null;

  private _breakdown = new BreakdownStateController(this);

  connectedCallback() {
    super.connectedCallback();
    void this._loadConfig();
    void this._breakdown.loadHierarchy();
  }

  private async _loadConfig(): Promise<void> {
    this._configLoading = true;
    try {
      const config = await lifeplanApi.getConfig();
      this._configured = !('isConfigured' in config && !config.isConfigured);
    } catch {
      this._configured = false;
    }
    this._configLoading = false;
  }

  private _switchTab(tab: ActiveTab): void {
    this._activeTab = tab;
  }

  private _hasBreakdownsForLevel(level: BreakdownLevel): boolean {
    const check = (nodes: BreakdownHierarchyNode[]): boolean => {
      for (const n of nodes) {
        if (n.level === level) return true;
        if (n.children.length > 0 && check(n.children)) return true;
      }
      return false;
    };
    return check(this._breakdown.hierarchy);
  }

  private _isLevelEnabled(info: BreakdownLevelInfo): boolean {
    if (!info.prerequisiteLevel) return true;
    return this._hasBreakdownsForLevel(info.prerequisiteLevel);
  }

  private _startWorkflow(level: BreakdownLevel): void {
    this._breakdown.reset();
    this._activeWorkflow = level;
  }

  private _closeWorkflow(): void {
    this._activeWorkflow = null;
    this._breakdown.reset();
  }

  private _findGoalById(goalId: number, nodes: BreakdownHierarchyNode[] = this._breakdown.hierarchy): BreakdownHierarchyNode | null {
    for (const node of nodes) {
      if (node.id === goalId) return node;
      const found = this._findGoalById(goalId, node.children);
      if (found) return found;
    }
    return null;
  }

  private _onGoalEdit(e: CustomEvent<{ goalId: number }>): void {
    const goal = this._findGoalById(e.detail.goalId);
    if (goal) this._editGoal = goal;
  }

  private _closeEditModal(): void {
    this._editGoal = null;
  }

  private _renderNotConfigured() {
    return html`
      <div class="cp-page-breakdowns__empty">
        <p class="cp-page-breakdowns__empty-text">Lebensplan nicht konfiguriert</p>
        <a class="cp-page-breakdowns__settings-link" href="#/settings">
          In den Settings konfigurieren
        </a>
      </div>
    `;
  }

  private _renderTabs() {
    return html`
      <div class="cp-page-breakdowns__tabs">
        <button
          class="cp-page-breakdowns__tab ${this._activeTab === 'workflows' ? 'cp-page-breakdowns__tab--active' : ''}"
          @click=${() => this._switchTab('workflows')}
        >Workflows</button>
        <button
          class="cp-page-breakdowns__tab ${this._activeTab === 'overview' ? 'cp-page-breakdowns__tab--active' : ''}"
          @click=${() => this._switchTab('overview')}
        >Uebersicht</button>
      </div>
    `;
  }

  private _renderWorkflowsTab() {
    if (this._activeWorkflow) {
      return this._renderActiveWorkflow();
    }

    return html`
      <div class="cp-page-breakdowns__levels">
        ${BREAKDOWN_LEVELS.map(info => {
          const enabled = this._isLevelEnabled(info);
          const exists = this._hasBreakdownsForLevel(info.level);

          return html`
            <div class="cp-page-breakdowns__level-card ${!enabled ? 'cp-page-breakdowns__level-card--disabled' : ''}">
              <div class="cp-page-breakdowns__level-header">
                <h3 class="cp-page-breakdowns__level-title">${info.label}</h3>
                ${exists ? html`<span class="cp-page-breakdowns__level-badge">Vorhanden</span>` : nothing}
              </div>
              <p class="cp-page-breakdowns__level-description">${info.description}</p>
              ${!enabled ? html`
                <p class="cp-page-breakdowns__level-hint">${info.prerequisiteLabel}</p>
              ` : html`
                <button
                  class="cp-page-breakdowns__level-btn"
                  @click=${() => this._startWorkflow(info.level)}
                >${exists ? 'Erneut durchfuehren' : 'Starten'}</button>
              `}
            </div>
          `;
        })}
      </div>
    `;
  }

  private _renderActiveWorkflow() {
    const levelInfo = BREAKDOWN_LEVELS.find(l => l.level === this._activeWorkflow);
    if (!levelInfo) return nothing;

    return html`
      <div class="cp-page-breakdowns__workflow">
        <div class="cp-page-breakdowns__workflow-header">
          <button
            class="cp-page-breakdowns__workflow-back"
            @click=${this._closeWorkflow}
          >&larr; Zurueck</button>
          <h3 class="cp-page-breakdowns__workflow-title">${levelInfo.label}</h3>
        </div>
        <div class="cp-page-breakdowns__workflow-content">
          <cp-breakdown-workflow
            .breakdownType=${this._activeWorkflow!}
            @workflow-close=${this._closeWorkflow}
          ></cp-breakdown-workflow>
        </div>
      </div>
    `;
  }

  private _renderOverviewTab() {
    return html`
      <div class="cp-page-breakdowns__overview">
        <cp-goal-hierarchy
          @start-workflow=${() => { this._switchTab('workflows'); }}
          @goal-edit=${this._onGoalEdit}
        ></cp-goal-hierarchy>
      </div>
    `;
  }

  render() {
    if (this._configLoading) {
      return html`
        <div class="cp-page cp-page-breakdowns">
          <h1 class="cp-page__title">Goal Breakdown</h1>
          <p class="cp-page-breakdowns__loading-text">Laden...</p>
        </div>
      `;
    }

    if (!this._configured) {
      return html`
        <div class="cp-page cp-page-breakdowns">
          <h1 class="cp-page__title">Goal Breakdown</h1>
          ${this._renderNotConfigured()}
        </div>
      `;
    }

    return html`
      <div class="cp-page cp-page-breakdowns">
        <h1 class="cp-page__title">Goal Breakdown</h1>
        ${this._breakdown.error ? html`
          <div class="cp-page-breakdowns__error">
            <p class="cp-page-breakdowns__error-text">${this._breakdown.error}</p>
          </div>
        ` : nothing}
        ${this._renderTabs()}
        <div class="cp-page-breakdowns__content">
          ${this._activeTab === 'workflows'
            ? this._renderWorkflowsTab()
            : this._renderOverviewTab()
          }
        </div>
      </div>
      <cp-goal-edit-modal
        .goal=${this._editGoal}
        .open=${this._editGoal !== null}
        @modal-closed=${this._closeEditModal}
        @goal-saved=${this._closeEditModal}
      ></cp-goal-edit-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-breakdowns': CpPageBreakdowns;
  }
}
